import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import cx from 'classnames';

const propTypes = {
  input: PropTypes.shape({
    value: PropTypes.string,
    onChange: PropTypes.func,
  }).isRequired,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
    pristine: PropTypes.bool,
  }),
  valueOptions: PropTypes.arrayOf(
    PropTypes.shape({ type: PropTypes.string, title: PropTypes.string })
  ),
  title: PropTypes.node,
  subtitle: PropTypes.node,
  hint: PropTypes.node,
  tooltip: PropTypes.node,
  disabled: PropTypes.bool,
  narrow: PropTypes.bool,
};

const defaultProps = {
  meta: {
    touched: false,
    error: null,
    pristine: true,
  },
  valueOptions: [
    { type: 'yes', title: 'toggle.yes' },
    { type: 'no', title: 'toggle.no' },
  ],
  title: null,
  subtitle: null,
  hint: null,
  tooltip: null,
  disabled: false,
  narrow: false,
};

export default function InputToggleField({
  input,
  meta: { touched, error, pristine },
  valueOptions,
  title,
  subtitle,
  hint,
  tooltip,
  disabled,
  narrow,
}) {
  const [animateOpacity, setAnimateOpacity] = useState(!!input.value);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipLocked, setTooltipLocked] = useState(false);

  const backgroundRef = useRef(null);
  const firstOptionRef = useRef(null);
  const secondOptionRef = useRef(null);
  const tooltipContainerRef = useRef(null);

  const updateBackground = useCallback(() => {
    if (!backgroundRef.current) return;

    const firstOption = firstOptionRef.current?.offsetWidth || 0;
    let width = 0;
    let left = 6;

    if (input.value === valueOptions[0].type) {
      width = firstOption;
      left = 6;
    } else if (input.value === valueOptions[1].type) {
      width = secondOptionRef.current?.offsetWidth || firstOption;
      left = firstOption + 6;
    }

    backgroundRef.current.style.width = `${width}px`;
    backgroundRef.current.style.left = `${left}px`;
  }, [input.value, valueOptions]);

  useEffect(() => {
    if (animateOpacity && input.value) {
      setAnimateOpacity(false);
    }
  }, [input.value]);

  useEffect(() => {
    updateBackground();
  }, [updateBackground]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        tooltipContainerRef.current
        && !tooltipContainerRef.current.contains(e.target)
      ) {
        setTooltipVisible(false);
        setTooltipLocked(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showTooltip = useCallback(() => {
    if (!tooltipLocked) setTooltipVisible(true);
  }, [tooltipLocked]);

  const hideTooltip = useCallback(() => {
    if (!tooltipLocked) setTooltipVisible(false);
  }, [tooltipLocked]);

  const toggleTooltip = useCallback(() => {
    setTooltipVisible((prev) => {
      const newVisible = !prev;
      setTooltipLocked(newVisible);
      return newVisible;
    });
  }, []);

  const classes = cx('toggle-control', {
    'toggle-control--narrow': narrow,
    'toggle-control--state-pristine': pristine,
    'toggle-control--state-disabled': disabled,
    'toggle-control--state-error': touched && error,
    'toggle-control--state-no-value': !input.value,
    'toggle-control--transition-opacity': animateOpacity,
  });

  return (
    <div className={classes}>
      <div
        className="toggle-control__description"
        ref={tooltipContainerRef}
      >
        {title && <span className="toggle-control__title">{title}</span>}
        {tooltip && (
          <button
            type="button"
            className="toggle-control__tooltip-trigger"
            onClick={toggleTooltip}
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
            aria-label="Show tooltip"
            aria-expanded={tooltipVisible}
          >
            i
          </button>
        )}
        {tooltipVisible && tooltip && (
          <div className="toggle-control__tooltip-box">
            <div
              className="toggle-control__tooltip-text"
              onClick={() => setTooltipLocked(true)}
            >
              {tooltip}
            </div>
          </div>
        )}
      </div>

      <div className="toggle-control__field">
        <div className="toggle-control__wrapper">
          <span
            className="toggle-control__background"
            ref={backgroundRef}
          />
          {valueOptions.map((opt, idx) => (
            <label
              key={opt.type}
              className="toggle-control__item"
              ref={idx === 0 ? firstOptionRef : secondOptionRef}
            >
              <input
                {...input}
                type="radio"
                className="toggle-control__item-input"
                value={opt.type}
                checked={input.value === opt.type}
                onChange={() => input.onChange(opt.type)}
              />
              <span className="toggle-control__item-wrapper">
                <span className="toggle-control__item-title">
                  <FormattedMessage id={opt.title} />
                </span>
              </span>
            </label>
          ))}
        </div>
      </div>

      {subtitle && <span className="toggle-control__subtitle">{subtitle}</span>}
      {hint && <span className="toggle-control__hint">{hint}</span>}
    </div>
  );
}

InputToggleField.propTypes = propTypes;
InputToggleField.defaultProps = defaultProps;
